'use client'

import { useFilteredStores } from 'maps/hooks/useFilteredStores/useFilteredStores'
import { useMapDispatch } from 'maps/hooks/useMapDispatch/useMapDispatch'
import { useMapState } from 'maps/hooks/useMapState/useMapState'
import { geocode } from 'maps/services/client/geocode/geocode'
import type { Coordinates, LatestSearchedLocation, Location } from 'maps/types'
import { STORAGE_KEY } from 'maps/utils/constants'
import { isDroppointType } from 'maps/utils/mapTypes/mapTypes'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useEffect } from 'react'
import {
	getFromSessionStorage,
	setToSessionStorage,
} from 'utils/webStorage/sessionStorage'

/** Hook that handles the latest searched location in session storage */
export function useStoredLocation(shouldGetLatestLocation: boolean) {
	const { location, type, searchInput } = useMapState()
	const dispatch = useMapDispatch()
	const { handleFilteredStores } = useFilteredStores()
	const {
		country: { countryCode, languageISO },
	} = useMasterData()

	const setLatestLocation = (
		latestSearchInput: string,
		latestLocation: Location | null
	) => {
		if (latestLocation) {
			setToSessionStorage(STORAGE_KEY, {
				searchInput: latestSearchInput,
				location: latestLocation,
			})
		}
	}

	const getLatestLocation = (): LatestSearchedLocation | null =>
		getFromSessionStorage(STORAGE_KEY)

	/**
	 * If the coordinates are defined, gets the corresponding address.
	 * If the address is defined, gets the corresponding coordinates.
	 */
	const handleGeocoding = async ({
		coordinates,
		address,
	}: {
		coordinates?: Coordinates
		address?: string
	}) => {
		const { addressInfo, coordinates: newCoordinates } =
			(await geocode({
				address,
				coordinates,
				countryCode,
				languageISO,
			})) ?? {}
		const newLocation = { coordinates: newCoordinates, addressInfo }
		handleFilteredStores(newLocation)
	}

	const handleLatestLocation = async () => {
		const latestLocation =
			getFromSessionStorage<LatestSearchedLocation>(STORAGE_KEY)
		if (latestLocation) {
			const { searchInput: previousSearchInput, location: previousLocation } =
				latestLocation
			const { addressInfo, coordinates } = previousLocation
			dispatch({
				searchInput: previousSearchInput,
			})

			if (isDroppointType(type) && !addressInfo) {
				await handleGeocoding({ coordinates })
				return
			}

			handleFilteredStores(previousLocation)
		}
	}

	useEffect(() => {
		if (location || !shouldGetLatestLocation) {
			return
		}

		if (searchInput) {
			void handleGeocoding({ address: searchInput })
			return
		}

		void handleLatestLocation()
	}, [])

	return { setLatestLocation, getLatestLocation }
}
